/**
 * Dialog Component
 *
 * A modal dialog built on Radix UI Dialog primitive.
 * Features glassmorphism styling, proper accessibility, and animations.
 * Focus trapping is handled by Radix UI's Dialog primitive internally.
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button>Open Dialog</Button>
 *   </DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Title</DialogTitle>
 *       <DialogDescription>Description</DialogDescription>
 *     </DialogHeader>
 *     <div>Content here</div>
 *     <DialogFooter>
 *       <Button>Save</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */

'use client';

import { getIconWithFallback } from '../lib/icon-resolver';
import { cn } from '../utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

const XIcon = getIconWithFallback('X');

// =============================================================================
// ROOT & PRIMITIVES
// =============================================================================

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

// =============================================================================
// OVERLAY
// =============================================================================

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      'fixed inset-0 z-50',
      'bg-(--overlay-bg)',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// =============================================================================
// CONTENT
// =============================================================================

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Whether to show the close button */
  showClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size = 'md', showClose = true, ...props }, ref) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-[var(--modal-max-width,600px)]',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[95vw]',
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          // Positioning
          'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
          // Size - responsive with margin on mobile
          'w-[calc(100%-32px)] sm:w-full max-h-[85vh]',
          sizeClasses[size],
          // Styling - Clean like dashboard cards (no border, shadow for depth)
          'bg-card',
          'rounded-(--card-border-radius)',
          'shadow-(--card-shadow)',
          'border-0', // Explicit border removal to override Tailwind preflight
          'overflow-y-auto p-[var(--modal-padding,24px)]',
          // Animations
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'duration-200',
          // Focus
          'focus:outline-none',
          className,
        )}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close
            className={cn(
              'absolute top-4 right-4',
              // Reduced size - was 32px, now 24px for a more compact close button
              'h-6 w-6 flex items-center justify-center',
              'rounded-[var(--radius-button,6.8px)]',
              'opacity-70 transition-opacity',
              'hover:opacity-100 hover:bg-accent',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:pointer-events-none',
            )}
          >
            {XIcon && <XIcon className="h-4 w-4" />}
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

// =============================================================================
// HEADER
// =============================================================================

const DialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="dialog-header"
    className={cn(
      'flex flex-col gap-1.5 text-center sm:text-left',
      'pb-4 mb-4',
      className,
    )}
    {...props}
  />
));
DialogHeader.displayName = 'DialogHeader';

// =============================================================================
// FOOTER
// =============================================================================

const DialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="dialog-footer"
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end',
      'gap-[var(--modal-footer-gap,8px)]',
      'pt-4 mt-4',
      className,
    )}
    {...props}
  />
));
DialogFooter.displayName = 'DialogFooter';

// =============================================================================
// TITLE
// =============================================================================

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    data-slot="dialog-title"
    className={cn(
      'text-[var(--modal-title-size,18px)]',
      'font-semibold',
      'leading-none tracking-tight',
      'text-(--modal-title-color)',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

// =============================================================================
// DESCRIPTION
// =============================================================================

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    data-slot="dialog-description"
    className={cn(
      'text-[var(--modal-description-size,14px)]',
      'text-(--modal-description-color)',
      'mt-1',
      className,
    )}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// =============================================================================
// EXPORTS
// =============================================================================

export {
    Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger,
};
